import React, { useState, useEffect, useCallback, useRef } from "react";
import DashboardHeader from "../../layouts/dashboard_header";
import SideMenu from "../../layouts/dashboard_menu";
import DashboardFooter from "../../layouts/dashboard_footer";
import ServiceBreadcrumb from "../../layouts/components/ServiceBreadcrumb";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPaperPlane, faEye, faSearch, faFilter, faSortUp, faSortDown, faSort,
    faHeartbeat, faStethoscope, faBrain, faTooth, faBaby, faMicroscope, 
    faDumbbell, faFemale, faProcedures, faAmbulance, faCalendarCheck, faUserCheck,
    faCheckCircle, faTimesCircle, faClock, faBuilding
} from '@fortawesome/free-solid-svg-icons';
import { useBaseUrl } from "../../../utils/utils";
import { useNavigate } from "react-router-dom";
import localForage from "localforage";

interface Application {
    id: number;
    service_request: {
        id: number;
        title: string;
        description: string;
        status: string;
        priority: string;
        engagement_type: string;
        patient_name?: string;
        created_at: string;
        start_date?: string;
        end_date?: string;
        min_pay?: number;
        max_pay?: number;
        response_time?: number;
        creator: {
            id: number;
            name: string;
            clinic_name: string;
        };
    };
    status: 'pending' | 'approved' | 'rejected' | 'withdrawn';
    created: string;
    proposed_rate?: number;
    proposed_timeline?: string;
    cover_letter?: string;
    qualifications?: string[];
    experience_years?: number;
}

const SentApplications: React.FC = () => {
    const baseUrl = useBaseUrl();
    const navigate = useNavigate();
    const [clinicId, setClinicId] = useState<number | null>(null);
    const [applications, setApplications] = useState<Application[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    
    // Filters and search
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('');
    const [sortField, setSortField] = useState<keyof Application>('created');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

    // Infinite scroll ref
    const observer = useRef<IntersectionObserver | undefined>(undefined);
    const lastApplicationRef = useRef<HTMLDivElement | null>(null);

    // Last application element callback for infinite scroll
    const lastApplicationCallback = useCallback((node: HTMLDivElement) => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    useEffect(() => {
        document.title = "Sent Applications - Uzima Healthcare";
        // Get clinic ID from localStorage
        localForage.getItem("clinic_id").then((clin: any) => {
            setClinicId(clin);
        });
    }, []);

    useEffect(() => {
        if (clinicId) {
            fetchApplications(true); // Reset and fetch first page
        }
    }, [clinicId, searchTerm, statusFilter, priorityFilter, sortField, sortDirection]);

    useEffect(() => {
        if (page > 1) {
            fetchApplications(false); // Fetch additional pages
        }
    }, [page]);

    const fetchApplications = async (reset: boolean = false) => {
        if (!clinicId) return;
        
        try {
            setLoading(true);
            const currentPage = reset ? 1 : page;
            
            const params = new URLSearchParams({
                page: currentPage.toString(),
                page_size: pageSize.toString(),
                search: searchTerm,
                status: statusFilter,
                priority: priorityFilter,
                sort_by: sortField,
                sort_direction: sortDirection,
                clinic_id: clinicId.toString()
            });

            const response = await fetch(`${baseUrl}collaborations/applications/sent/?${params}`);
            if (response.ok) {
                const data = await response.json();
                const newApplications = data.data?.applications || [];
                
                if (reset) {
                    setApplications(newApplications);
                    setPage(1);
                } else {
                    setApplications(prev => [...prev, ...newApplications]);
                }
                
                setHasMore(newApplications.length === pageSize);
            } else {
                console.error('Failed to fetch sent applications');
                if (reset) setApplications([]);
            }
        } catch (error) {
            console.error('Error fetching sent applications:', error);
            if (reset) setApplications([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field: keyof Application) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter('');
        setPriorityFilter('');
        setSortField('created');
        setSortDirection('desc');
    };

    const getRequestIcon = (title: string, engagementType: string) => {
        const lowerTitle = title.toLowerCase();
        
        if (lowerTitle.includes('cardio') || lowerTitle.includes('heart')) return faHeartbeat;
        if (lowerTitle.includes('consultation') || lowerTitle.includes('checkup')) return faStethoscope;
        if (lowerTitle.includes('neuro') || lowerTitle.includes('brain')) return faBrain;
        if (lowerTitle.includes('dental') || lowerTitle.includes('tooth')) return faTooth;
        if (lowerTitle.includes('pediatric') || lowerTitle.includes('child')) return faBaby;
        if (lowerTitle.includes('lab') || lowerTitle.includes('test')) return faMicroscope;
        if (lowerTitle.includes('physio') || lowerTitle.includes('therapy')) return faDumbbell;
        if (lowerTitle.includes('gyneco') || lowerTitle.includes('women')) return faFemale;
        if (lowerTitle.includes('surgery') || lowerTitle.includes('operation')) return faProcedures;
        if (lowerTitle.includes('emergency') || lowerTitle.includes('urgent')) return faAmbulance;
        if (lowerTitle.includes('appointment') || lowerTitle.includes('schedule')) return faCalendarCheck;
        
        return faStethoscope;
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'pending': return 'warning';
            case 'approved': return 'success';
            case 'rejected': return 'danger';
            case 'withdrawn': return 'secondary';
            default: return 'secondary';
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority.toLowerCase()) {
            case 'low': return 'success';
            case 'normal': return 'info';
            case 'high': return 'warning';
            case 'urgent': return 'danger';
            default: return 'secondary';
        }
    };

    const getApplicationStatusIcon = (status: string) => {
        switch (status.toLowerCase()) {
            case 'pending': return faClock;
            case 'approved': return faCheckCircle;
            case 'rejected': return faTimesCircle;
            case 'withdrawn': return faTimesCircle;
            default: return faClock;
        }
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className={"vertical-overlay"} />
            <div className={"main-content"}>
                <div className={"page-content"}>
                    <div className={"container-fluid"}>
                        <ServiceBreadcrumb currentPage="Sent Applications" />
                        <div className="row mb-4 align-items-center">
                            <div className="col-8">
                                <h2 className="fw-bold mb-0">
                                    <FontAwesomeIcon icon={faPaperPlane} className="me-2 text-primary" />
                                    Sent Applications
                                </h2>
                                <div className="text-muted">Service requests you have applied for (pending, rejected, or closed)</div>
                            </div>
                            <div className="col-4 text-end">
                                <span className="badge bg-primary fs-6">
                                    {applications.length} applications
                                </span>
                            </div>
                        </div>

                        {/* Search and Filters */}
                        <div className="card mb-4">
                            <div className="card-body">
                                <div className="row g-3 align-items-end">
                                    <div className="col-md-4">
                                        <label className="form-label">Search</label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <FontAwesomeIcon icon={faSearch} />
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search applications..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label">Status</label>
                                        <select
                                            className="form-select"
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                        >
                                            <option value="">All Statuses</option>
                                            <option value="pending">Pending</option>
                                            <option value="approved">Approved</option>
                                            <option value="rejected">Rejected</option>
                                            <option value="withdrawn">Withdrawn</option>
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label">Priority</label>
                                        <select
                                            className="form-select"
                                            value={priorityFilter}
                                            onChange={(e) => setPriorityFilter(e.target.value)}
                                        >
                                            <option value="">All Priorities</option>
                                            <option value="low">Low</option>
                                            <option value="normal">Normal</option>
                                            <option value="high">High</option>
                                            <option value="urgent">Urgent</option>
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <button
                                            className="btn btn-outline-secondary w-100"
                                            onClick={clearFilters}
                                        >
                                            <FontAwesomeIcon icon={faFilter} className="me-1" />
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Applications Grid */}
                        <div className="row g-4">
                            {applications.map((application, index) => (
                                <div 
                                    key={application.id} 
                                    className="col-lg-6 col-xl-4"
                                    ref={index === applications.length - 1 ? lastApplicationCallback : undefined}
                                >
                                    <div 
                                        className="card h-100 border-0 shadow-sm position-relative overflow-hidden"
                                        style={{ 
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                                        }}
                                        onClick={() => navigate(`/institution/service-center?tab=applications&appTab=sent&appId=${application.id}`)}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-5px)';
                                            e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                                        }}
                                    >
                                        {/* Application Status Badge */}
                                        <div className="position-absolute top-0 end-0 p-3">
                                            <span className={`badge bg-${getStatusColor(application.status)} fs-6 px-3 py-2`}>
                                                <FontAwesomeIcon 
                                                    icon={getApplicationStatusIcon(application.status)} 
                                                    className="me-1" 
                                                />
                                                {application.status}
                                            </span>
                                        </div>

                                        {/* Request Priority Badge */}
                                        <div className="position-absolute top-0 start-0 p-3">
                                            <span className={`badge bg-${getPriorityColor(application.service_request.priority)} fs-6 px-3 py-2`}>
                                                {application.service_request.priority}
                                            </span>
                                        </div>

                                        <div className="card-body p-4">
                                            {/* Icon and Title */}
                                            <div className="text-center mb-4">
                                                <div 
                                                    className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                                    style={{
                                                        width: '80px',
                                                        height: '80px',
                                                        background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                                                        color: 'white',
                                                        boxShadow: '0 8px 25px rgba(255, 236, 210, 0.3)'
                                                    }}
                                                >
                                                    <FontAwesomeIcon 
                                                        icon={getRequestIcon(application.service_request.title, application.service_request.engagement_type)} 
                                                        size="2x"
                                                    />
                                                </div>
                                                <h5 className="card-title fw-bold text-dark mb-2" style={{ fontSize: '1.1rem' }}>
                                                    {application.service_request.title}
                                                </h5>
                                            </div>

                                            {/* Description */}
                                            <p className="card-text text-muted mb-3" style={{ fontSize: '0.9rem', lineHeight: '1.5' }}>
                                                {application.service_request.description.length > 100 
                                                    ? `${application.service_request.description.substring(0, 100)}...` 
                                                    : application.service_request.description
                                                }
                                            </p>

                                            {/* Engagement Type */}
                                            <div className="text-center mb-3">
                                                <span className="badge bg-info fs-6 px-3 py-2">
                                                    {application.service_request.engagement_type}
                                                </span>
                                            </div>

                                            {/* Request Creator Info */}
                                            <div className="text-center mb-3">
                                                <small className="text-muted d-block">
                                                    <FontAwesomeIcon icon={faBuilding} className="me-1" />
                                                    Request Creator
                                                </small>
                                                <div className="fw-bold text-dark">
                                                    {application.service_request.creator.name}
                                                </div>
                                                <small className="text-muted">
                                                    {application.service_request.creator.clinic_name}
                                                </small>
                                            </div>

                                            {/* Proposed Rate */}
                                            {application.proposed_rate && (
                                                <div className="text-center mb-3">
                                                    <small className="text-muted d-block">Your Proposed Rate</small>
                                                    <div className="fw-bold text-success">
                                                        Ksh {application.proposed_rate.toLocaleString()}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Payment Range */}
                                            {application.service_request.min_pay && application.service_request.max_pay && (
                                                <div className="text-center mb-3">
                                                    <small className="text-muted d-block">Requested Payment Range</small>
                                                    <div className="fw-bold text-info">
                                                        Ksh {application.service_request.min_pay.toLocaleString()} - {application.service_request.max_pay.toLocaleString()}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Response Time */}
                                            {application.service_request.response_time && (
                                                <div className="text-center mb-3">
                                                    <small className="text-muted d-block">Response Time</small>
                                                    <div className="fw-bold text-warning">
                                                        {application.service_request.response_time} hours
                                                    </div>
                                                </div>
                                            )}

                                            {/* Patient Info */}
                                            {application.service_request.patient_name && (
                                                <div className="text-center mb-3">
                                                    <small className="text-muted">
                                                        Patient: {application.service_request.patient_name}
                                                    </small>
                                                </div>
                                            )}

                                            {/* Dates */}
                                            <div className="row text-center mb-3">
                                                <div className="col-6">
                                                    <small className="text-muted d-block">Applied</small>
                                                    <small className="fw-bold text-dark">
                                                        {new Date(application.created).toLocaleDateString()}
                                                    </small>
                                                </div>
                                                <div className="col-6">
                                                    <small className="text-muted d-block">Request Created</small>
                                                    <small className="fw-bold text-dark">
                                                        {new Date(application.service_request.created_at).toLocaleDateString()}
                                                    </small>
                                                </div>
                                            </div>

                                            {/* Action Button */}
                                            <div className="text-center">
                                                <button 
                                                    className="btn btn-primary btn-sm px-4 py-2"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/institution/service-center?tab=applications&appTab=sent&appId=${application.id}`);
                                                    }}
                                                    style={{
                                                        background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                                                        border: 'none',
                                                        borderRadius: '25px',
                                                        boxShadow: '0 4px 15px rgba(255, 236, 210, 0.3)'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faEye} className="me-2" />
                                                    View Application
                                                </button>
                                            </div>
                                        </div>

                                        {/* Decorative Elements */}
                                        <div 
                                            className="position-absolute bottom-0 start-0 w-100 h-100"
                                            style={{
                                                background: 'linear-gradient(135deg, transparent 0%, rgba(255, 236, 210, 0.05) 100%)',
                                                pointerEvents: 'none'
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Loading Indicator */}
                        {loading && (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <div className="mt-2 text-muted">Loading more applications...</div>
                            </div>
                        )}

                        {/* No More Data */}
                        {!hasMore && applications.length > 0 && (
                            <div className="text-center py-4">
                                <div className="text-muted">
                                    <FontAwesomeIcon icon={faPaperPlane} size="2x" className="mb-3" />
                                    <h6>No more applications to load</h6>
                                    <p>You've reached the end of your sent applications.</p>
                                </div>
                            </div>
                        )}

                        {/* Empty State */}
                        {!loading && applications.length === 0 && (
                            <div className="text-center py-5">
                                <FontAwesomeIcon icon={faPaperPlane} size="3x" className="mb-3 text-primary" />
                                <h5>No sent applications found</h5>
                                <p className="text-muted">You haven't applied for any service requests yet.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <DashboardFooter />
        </>
    );
};

export default SentApplications;
